import {
  ButtonDS,
  EditOutlined,
  SidePanelDS,
  Spacer,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { AdvancedAnalyticsVehicles } from "../fleetApi";
import "../../../../i18n/i18n";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";

interface ContractDataSectionProps {
  vehicle: Pick<
    AdvancedAnalyticsVehicles,
    | "additionalCostPerKm"
    | "contractMileage"
    | "contractStartDate"
    | "contractEndDate"
  >;
  onEdit: () => void;
}

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledSubTitle = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

export const ContractDataSection = ({
  vehicle,
  onEdit,
}: ContractDataSectionProps) => {
  const { t } = useTranslation();

  return (
    <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
      <StyledSectionHeader>
        <SectionTitle>
          <TextCapitalized>{t("vehicles.sidepanel.contract")}</TextCapitalized>
        </SectionTitle>
        <ButtonDS
          sizeButton="S"
          format="hug"
          buttonType="secondary"
          singleIcon={{ icon: <EditOutlined />, size: "S" }}
          onClick={onEdit}
        />
      </StyledSectionHeader>
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("vehicles.sidepanel.additionalCostPerKm")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          vehicle.additionalCostPerKm !== null ? (
            <StyledRow>
              {vehicle.additionalCostPerKm}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.additionalCostPerKm.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          ) : (
            "-"
          )
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("vehicles.sidepanel.contractMileage")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          vehicle.contractMileage !== null ? (
            <StyledRow>
              {vehicle.contractMileage}
              <Spacer x={0.25} />
              <StyledSubTitle>
                <TextCapitalized>
                  {t("vehicles.sidepanel.mileage.units")}
                </TextCapitalized>
              </StyledSubTitle>
            </StyledRow>
          ) : (
            "-"
          )
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("vehicles.sidepanel.contractStartDate")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          vehicle.contractStartDate
            ? dateFormatterDayMonthYearLong2Digits(
                new Date(vehicle.contractStartDate),
              )
            : "-"
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("vehicles.sidepanel.contractEndDate")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          vehicle.contractEndDate
            ? dateFormatterDayMonthYearLong2Digits(
                new Date(vehicle.contractEndDate),
              )
            : "-"
        }
      />
    </SidePanelDS.Section>
  );
};

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
